import * as React from "react";
import { DataFilterTable } from "Components/Form/DataFilterTable";
import { observer } from "mobx-react";
import { ColumnProps } from "antd/lib/table";
import { SorterResult, SortOrder } from "antd/lib/table/interface";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Badge, Dropdown, Tag, Tooltip } from "antd";
import { TableColumnHeader } from "Components/General/ColumnHeaderTooltip";
import { PaginationProps } from "antd/lib/pagination";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { userInfoStore } from "Stores/UserInfoStore";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";

class ProjectColumn extends React.Component<ColumnProps<API_AdminProject>> {}

export interface ProjectQueryParams extends API_AdminProjectFilter, API_AdminProjectPagination {}

interface ProjectTemplateOverviewProps extends WithTranslation, WithRouterProps {
  // if this prop is present - component is used by PGADMIN so he should not be allowed
  // to create pgroups or see templates outside this group (filter in GET request)
  projectGroupId?: number;
}

interface ProjectOverViewTableState {
  sorter: SorterResult<API_AdminProject>;
  data: API_AdminProject[];
  loading: boolean;
  pagination: PaginationProps;
  searchString: string;
}

export const ProjectTemplateOverview = withTranslation([I18N_NS.ADMIN, I18N_NS.PROJECT])(
  withRouter(
    observer(
      class extends React.Component<ProjectTemplateOverviewProps, ProjectOverViewTableState> {
        private API_PROJECTS_ADMIN = {
          ENABLED: "Enabled",
          DEACTIVATED: "Deactivated",
          ARCHIVED: "Archived",
          PROTECTED: "Protected",
          HIGH_SECURE: "High secure",
          HSM: "High secure (HSM)",
          NONE: "Default",
          MODERN: "Modern",
          CLASSIC: "Classic",
        };
        constructor(props: ProjectTemplateOverviewProps) {
          super(props);
          this.state = {
            searchString: "",
            data: [],
            sorter: null,
            loading: false,
            pagination: {
              total: 0,
              current: 1,
              pageSize: 10,
            },
          };
        }

        componentDidMount() {
          this.handleTableChange(this.state.pagination, {}, this.state.sorter);
        }

        getDropdownActionsForProject = (record: API_AdminProject) => {
          if (this.props.projectGroupId) {
            return [
              {
                label: "Access project",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/project/${prId}/overview`),
                disabled: record.unassignable,
              },
            ];
          } else {
            return [
              {
                label: "Access project",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/project/${prId}/overview`),
                disabled: record.unassignable,
              },
              {
                label: "Clone project",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/templates/${prId}/clone`),
              },
              {
                label: "Filename rules",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(
                    `${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/filename-rules`
                  ),
                disabled: !record.functions?.FILENAMECHECK,
              },
              {
                label: "Metadata",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/metadata`),
                disabled: !record.functions?.METADATA,
              },
              {
                label: "Repro services",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(
                    `${window.location.origin}/${window.GLOBAL.API_BASE}/admin/templates/${prId}/repro-service`
                  ),
              },
              {
                label: "Views",
                disabled: !record.modules.VIEW,
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/views`),
              },
              {
                label: "Auto-Organise",
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(
                    `${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/auto-organise`
                  ),
                disabled: !record.modules.AUTOORGANIZE,
              },
              {
                label: "Manage workflow",
                disabled: !record.modules.WORKFLOW,
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/workflows`),
              },
              {
                label: "Directory mapping",
                disabled: !record.functions.AUTOMATICDIRMAPPING,
                // tslint:disable-next-line:max-line-length
                onClick: (prId: number) =>
                  window.open(
                    `${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/auto-mapping`
                  ),
              },
            ];
          }
        };

        handleTableChange = async (
          pagination: PaginationProps,
          filters: {},
          sorter: SorterResult<API_AdminProject>
        ) => {
          try {
            this.setState({ loading: true });
            const sortByMap: {
              [key in "id" | "shortname" | "name" | "description" | "type"]: API_ADMINPROJECTPAGINATION_SORTBY;
            } = {
              id: "ID",
              shortname: "SHORT_NAME",
              name: "NAME",
              description: "DESCRIPTION",
              type: "TYPE",
            };
            const sortOrderMap: { [key in SortOrder]: API_ADMINPROJECTPAGINATION_SORTORDER } = {
              ascend: "ASC",
              descend: "DESC",
            };
            const params: ProjectQueryParams = {
              limit: pagination.pageSize,
              offset: pagination.current > 1 ? pagination.pageSize * (pagination.current - 1) : 0,
              // by default set sorted by userId and ascending.
              sortBy: sorter ? sortByMap[sorter.field as string] : "ID",
              sortOrder: sorter ? sortOrderMap[sorter.order] : "ASC",
              query: this.state.searchString,
              projectGroupId: this.props.projectGroupId,
            };
            const response: API_MultiAdminProjectResponse = await API.getData(
              API_URL.ADMIN_TEMPLATES.getAllTemplates(),
              params
            );
            this.setState({ pagination: { ...pagination, total: response.total }, sorter, data: response.items || [] });
          } catch (error) {
            throw error;
          } finally {
            this.setState({ loading: false });
          }
        };

        handleSearch = (searchValue: string) => {
          const { pagination, searchString } = this.state;
          // Reset current page to 1 when trying to reset or change search query
          if (!searchValue || searchValue !== searchString) {
            pagination.current = 1;
          }
          this.setState({ searchString: searchValue }, () => {
            this.handleTableChange(pagination, {}, this.state.sorter);
          });
        };

        renderDropdownActionButtons = (text, record) => {
          const { t } = this.props;
          const dropdownActions = this.getDropdownActionsForProject(record);

          const onClick = ({ key }) => {
            dropdownActions[key].onClick(record.id);
          };

          return (
            <Dropdown
              menu={{
                items: dropdownActions.map((item, idx) => ({
                  key: idx,
                  label: t(item.label),
                })),
                onClick: onClick,
              }}
              placement="bottomRight"
            >
              <span style={{ display: "inline-block", width: 60, textAlign: "center" }}>
                <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
              </span>
            </Dropdown>
          );
        };

        renderHeaderCell = (sortOrder: SortOrder = "ascend", title: string, columnIndex: string) => {
          const { t } = this.props;
          const { sorter } = this.state;
          if (sorter && sorter.columnKey === columnIndex) {
            return <TableColumnHeader title={t(title)} sortOrder={sortOrder} />;
          }
          return t(title);
        };

        linkCell = (text: string, record: API_AdminProject) => {
          return this.props.projectGroupId ? text : <Link to={`/admin/templates/edit/${record.id}`}>{text}</Link>;
        };

        convertEnumToText = (text: string) => this.props.t(this.API_PROJECTS_ADMIN[text]) || text;

        convertStatusEnumToBadge = (text: string, record: API_AdminProject) => {
          const { t } = this.props;
          switch (record.status) {
            case "ENABLED":
              return <Badge status="success" text={t("Active")} />;
            case "ARCHIVED":
              return <Badge status="warning" text={t("Archived")} />;
            case "DEACTIVATED":
              return <Badge status="error" text={t("Deactivated")} />;
            default:
              return null;
          }
        };
        projectExtras = (text, record): React.ReactNode => {
          return text ? (
            <FontAwesomeIcon
              icon={["fas", "comment-exclamation"]}
              style={{ color: record.status === "ENABLED" && "#3d3d3d" }}
            />
          ) : (
            ""
          );
        };

        projectType = (text: string): React.ReactNode => {
          const type = text === "NONE" ? "-" : text;
          return this.convertEnumToText(type);
        };

        projectGroupName = (text: string, record: API_AdminProject, index: number) => {
          return text ? (
            <Tooltip title={text}>
              <Tag key={index} className="maxWidth-overflow-cell">
                {text}
              </Tag>
            </Tooltip>
          ) : null;
        };

        render() {
          const { t } = this.props;
          const { data, pagination, loading } = this.state;

          return (
            <DataFilterTable
              name="ProjectTemplateOverview"
              rowKey="id"
              searchOnChange={false}
              dataSource={data}
              pagination={pagination.total < pagination.pageSize ? false : pagination}
              loading={loading}
              rowClassName={(record) =>
                classNames(
                  "",
                  { disabledCell: record.status === "DEACTIVATED" },
                  { greyCell: record.status !== "ENABLED" },
                  { rowDisableClick: userInfoStore.role === "PRGROUPADMIN" && !this.props.projectGroupId }
                )
              }
              onChange={this.handleTableChange}
              onSearch={this.handleSearch}
              placeholder={t("Search in id, names, descr., extras, customer")}
              actionButtons={
                this.props.projectGroupId
                  ? undefined
                  : [
                      {
                        iconName: "plus",
                        onClick: () => this.props.navigate("/admin/templates/new"),
                        label: t("New project template"),
                      },
                    ]
              }
            >
              <ProjectColumn
                title={({ sortOrder }) => this.renderHeaderCell(sortOrder, "Project id", "id")}
                dataIndex={"id"}
                defaultSortOrder={"ascend"}
                render={this.convertEnumToText}
                sorter={true}
              />
              <ProjectColumn
                title={({ sortOrder }) => this.renderHeaderCell(sortOrder, "Name", "name")}
                dataIndex={"name"}
                render={this.linkCell}
                sorter={true}
              />
              <ProjectColumn
                title={({ sortOrder }) => this.renderHeaderCell(sortOrder, "Projectgroup", "prGroupShortName")}
                dataIndex={"prGroupShortName"}
                render={this.projectGroupName}
                sorter={false}
              />
              <ProjectColumn
                title={({ sortOrder }) => this.renderHeaderCell(sortOrder, "Extras", "extras")}
                dataIndex={"extras"}
                render={this.projectExtras}
                sorter={true}
              />
              <ProjectColumn
                title={({ sortOrder }) => this.renderHeaderCell(sortOrder, "Status", "status")}
                dataIndex={"status"}
                render={this.convertStatusEnumToBadge}
                sorter={true}
              />
              <ProjectColumn width={74} title={t("Actions")} render={this.renderDropdownActionButtons} />
            </DataFilterTable>
          );
        }
      }
    )
  )
);
